import { css } from '@emotion/react';
import { Box, Button, Input, Option, Select, Stack, styled, Typography } from '@mui/joy';
import { RowData, Table } from '@tanstack/react-table';

const Root = styled(Box)(
  ({ theme }) => css`
    margin-top: auto;
    position: sticky;
    bottom: 0;
    z-index: 101;
    border-top: 2px solid ${theme.palette.divider};
    left: 0;
    right: 0;
  `,
);

const Inner = styled(Box)(
  ({ theme }) => css`
    align-items: center;
    padding: ${theme.spacing(2)};
    padding-bottom: ${theme.spacing(1.75)};
    background-color: ${theme.palette.background.surface};

    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};

    ${theme.breakpoints.up('md')} {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
    }
  `,
);

const StyledButton = styled(Button)(
  () => css`
    line-height: 1;
  `,
);
StyledButton.defaultProps = {
  size: 'sm',
  variant: 'outlined',
};

type TableFooterProps<TData extends RowData> = {
  table: Table<TData>;
  subject: string;
};

export function TableFooter<TData extends RowData>({ table, subject }: TableFooterProps<TData>) {
  return (
    <Root>
      <Inner>
        <Stack direction="row" gap={1} alignItems="center">
          {table.getRowCount() > 0 && table.getPageCount() > 1 && (
            <>
              <StyledButton onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                «
              </StyledButton>
              <StyledButton onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                ‹
              </StyledButton>

              <Typography>Seite</Typography>
              <Input
                type="number"
                aria-label="Seitennummer"
                size="sm"
                variant="outlined"
                slotProps={{
                  input: {
                    min: 1,
                    max: table.getPageCount(),
                    style: { textAlign: 'right' },
                  },
                }}
                value={table.getState().pagination.pageIndex + 1}
                onChange={(event) => {
                  const page = event.target.value ? Number(event.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                sx={{ display: 'inline-flex' }}
              />
              <Typography whiteSpace="nowrap">von {table.getPageCount()}</Typography>

              <StyledButton onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                ›
              </StyledButton>
              <StyledButton
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                »
              </StyledButton>
            </>
          )}
        </Stack>

        <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
          <Typography textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
            <strong>{table.getRowCount()}</strong> {subject}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
          {table.getRowCount() > 10 && (
            <>
              <Typography>Zeige</Typography>
              <Select
                aria-label="Einträge pro Seite"
                size="sm"
                variant="outlined"
                value={table.getState().pagination.pageSize}
                onChange={(event, value) => {
                  if (value) {
                    table.setPageSize(value);
                  }
                }}
              >
                {[10, 25, 50, 100].map((pageSizeItem) => (
                  <Option key={pageSizeItem} value={pageSizeItem}>
                    {pageSizeItem}
                  </Option>
                ))}
              </Select>
            </>
          )}
        </Stack>
      </Inner>
    </Root>
  );
}
